import React from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { ConfirmInvoiceStatusDateModal } from "../ConfirmInvoiceStatusDateModal";

interface Props {
    show: boolean;
    selectedCount: number;
    /**
     * True only when every selected invoice posts a bad-debt journal entry
     * (all USD). When the selection includes a non-USD invoice we drop the
     * bad-debt claim, since those invoices generate no journal entry — the
     * backend JE builder skips them. See `invoiceBooksToBadDebt`.
     */
    allBookToBadDebt: boolean;
    isProcessing: boolean;
    /**
     * The latest `invoiceDate` across every selected invoice (native
     * invoices only — every other source has `invoiceDate: null` and
     * doesn't constrain this), so the picker disables dates before it —
     * matching the single-invoice "uncollectableAt on/after invoiceDate"
     * rule (see `assertDatesOnOrAfterInvoiceDate`) for the whole selection.
     */
    minDate?: string;
    /**
     * The latest period lock date across every selected invoice's entity
     * (see `getLatestLockDate`), so the picker disables dates locked for
     * ANY of them — not just one.
     */
    lockDate?: Date | null;
    /**
     * False while the selected entities' lock query is still loading — keeps
     * Confirm disabled until the latest lock date is known, so a locked-period
     * date can't be submitted during that window.
     */
    isLockResolved?: boolean;
    onCancel: () => void;
    onConfirm: (date: string) => void;
}

const RECOVERY_NOTE =
    "Uncollectible invoices can be matched or marked paid later if recovered.";

// Reuses the same date-selection modal the single-invoice status dropdown
// uses for Uncollectible (ConfirmInvoiceStatusDateModal) — see
// InvoiceStatusSelect. Both floors — the invoice-date one (minDate) and the
// period-lock one (lockDate) — are enforced here client-side up front,
// combined via ConfirmInvoiceStatusDateModal's
// `laterDateOnly(minDate, firstUnlockedDate)` so the effective floor is
// whichever is later across the whole selection.
export const BulkUncollectibleInvoicesModal: React.FC<Props> = ({
    show,
    selectedCount,
    allBookToBadDebt,
    isProcessing,
    minDate,
    lockDate,
    isLockResolved,
    onCancel,
    onConfirm,
}) => (
    <ConfirmInvoiceStatusDateModal
        show={show}
        title={`Mark ${pluralizeWithCount("invoice", selectedCount)} uncollectible`}
        description={
            allBookToBadDebt
                ? `The open balance of each invoice will be booked to bad debt expense on the uncollectible date. ${RECOVERY_NOTE}`
                : RECOVERY_NOTE
        }
        dateLabel="Uncollectible date"
        confirmLabel="Mark uncollectible"
        isProcessing={isProcessing}
        minDate={minDate}
        lockDate={lockDate}
        isLockResolved={isLockResolved}
        onCancel={onCancel}
        onConfirm={onConfirm}
    />
);
